import { useRoute } from 'vue-router'
import { nextTick, onMounted, reactive, ref, watch } from 'vue'
import usePost from '../../../core/composables/api/usePost'
import { ApiRequestStatus } from '../../../core/models/api/api-request-status'
import { useSnackStore } from '../../../stores/snack.store'
import { useEventBus } from '@vueuse/core'
import { EventKey } from '../../../core/events/event-key'
import type { PulseAnalysis } from '@/core/models/surveys-analytics'

export const usePulseFeedbackTopicSlideAnalysis = (
  feedbackTopicId: string,
  allSurveyIds: string[],
  slideProperties: SlideProperties,
) => {
  const route = useRoute()
  const dialogId = route.params.id as string
  const snackStore = useSnackStore()
  const eventBus = useEventBus(EventKey.dialogSlideUpdated)

  const {
    data: analysisData,
    status,
    mutate: loadAnalysis,
  } = usePost<Omit<PulseAnalysis, 'texts'>, { surveyIds: string[] }>(
    `dialog/prepare/${dialogId}/pulse-slide/${feedbackTopicId}/analysis`,
  )

  const { mutate: saveSlide, status: saveStatus } = usePost<void, PulseSlideInput>(
    `dialog/prepare/${dialogId}/pulse-slide/${feedbackTopicId}`,
  )

  const hasChanged = ref(false)
  const isInitializing = ref(true)

  const inputData = reactive<PulseSlideInput>({
    surveyIds: [],
  })

  const hydrateForm = () => {
    isInitializing.value = true

    if (slideProperties.updatedAt) {
      inputData.surveyIds = [...slideProperties.surveyIds]
    } else {
      inputData.surveyIds = [...allSurveyIds]
    }

    nextTick(() => {
      isInitializing.value = false
      hasChanged.value = false
    })
  }

  const reloadAnalysis = (surveyIds: string[]) => {
    return loadAnalysis({ surveyIds })
  }

  const updateSlide = async () => {
    await saveSlide({ surveyIds: inputData.surveyIds })

    if (saveStatus.value === ApiRequestStatus.Success) {
      snackStore.showInfo('Daten gespeichert.')
      eventBus.emit({ dialogId, slideId: feedbackTopicId })
      hasChanged.value = false
    } else if (saveStatus.value === ApiRequestStatus.Error) {
      snackStore.showError('Daten nicht gespeichert.')
    }
  }

  onMounted(async () => {
    hydrateForm()
    await reloadAnalysis(inputData.surveyIds)
  })

  watch(
    inputData,
    () => {
      if (isInitializing.value) return

      hasChanged.value = true
    },
    { deep: true },
  )

  return {
    data: analysisData,
    status,
    inputData,
    hasChanged,
    reloadAnalysis,
    updateSlide,
  }
}

export interface PulseSlideInput {
  surveyIds: string[]
}

interface SlideProperties {
  updatedAt: string | null
  surveyIds: ReadonlyArray<string>
}
